import { useMemo, useState } from 'react'
import {
  Box, Button, CircularProgress, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined'
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined'
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined'
import { LineChart } from '@mui/x-charts/LineChart'
import type { ChartsAxisData } from '@mui/x-charts/models'
import { useQuery } from '@tanstack/react-query'
import type { AssetWorkOrderHistoryEntry } from '@cmms/types'

import { api } from '../../../lib/api'
import { useOrgSettings } from '../../../hooks/useOrgSettings'
import { SearchableSelect } from '../../../components/SearchableSelect'
import { UserAvatar } from '../../../components/UserAvatar'
import { PRIORITY_META, TYPE_LABEL, TYPE_COLOR, getStatusMeta } from '../../work-orders/dummy'
import { FilterChip } from '../AssetsPage'

const WORK_TYPES = ['preventive', 'reactive', 'inspection', 'safety', 'other'] as const
const APPROVAL_STATUS_COLOR: Record<string, string> = { pending: '#D97706', approved: '#059669', rejected: '#DC2626' }
const chartTooltipSx = { '& .MuiChartsTooltip-table': { fontSize: 12 } }

function typeColor(t: string): string { return TYPE_COLOR[t] ?? TYPE_COLOR.other! }
function typeLabel(t: string): string { return TYPE_LABEL[t] ?? 'Other' }

interface Option { id: string; name: string }

type ViewMode = 'list' | 'chart'

interface Props {
  assetId: string
  onNavigate: (path: string) => void
}

export function AssetWorkOrderHistory({ assetId, onNavigate }: Props) {
  const { formatDate } = useOrgSettings()
  const woQ = useQuery({
    queryKey: ['asset-work-order-history', assetId],
    queryFn: () => api.assets.getWorkOrderHistory(assetId),
    staleTime: 30_000,
  })
  const all: AssetWorkOrderHistoryEntry[] = Array.isArray(woQ.data) ? woQ.data : []

  const [view, setView] = useState<ViewMode>('list')
  const [workType, setWorkType] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [locationId, setLocationId] = useState('')
  const [executorId, setExecutorId] = useState('')
  const [reviewerId, setReviewerId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Filter option lists are derived from this asset's own history —
  // scoped to people/locations that actually appear here, not every
  // user/location in the org.
  const locationOptions = useMemo(() => {
    const map = new Map<string, string>()
    all.forEach((wo) => { if (wo.location_id && wo.location_name) map.set(wo.location_id, wo.location_name) })
    return [...map.entries()].map(([id, name]): Option => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [all])

  const executorOptions = useMemo(() => {
    const map = new Map<string, string>()
    all.forEach((wo) => {
      if (wo.assigned_to_user_id && wo.assigned_to_user_name) map.set(wo.assigned_to_user_id, wo.assigned_to_user_name)
      ;(wo.assignees ?? []).forEach((a) => map.set(a.id, a.name))
    })
    return [...map.entries()].map(([id, name]): Option => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [all])

  const reviewerOptions = useMemo(() => {
    const map = new Map<string, string>()
    all.forEach((wo) => { if (wo.reporter_id && wo.reporter_name) map.set(wo.reporter_id, wo.reporter_name) })
    return [...map.entries()].map(([id, name]): Option => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [all])

  const filtered = useMemo(() => {
    return all.filter((wo) => {
      if (workType !== 'all' && wo.work_type !== workType) return false
      if (statusFilter !== 'all' && wo.status !== statusFilter) return false
      if (priorityFilter !== 'all' && wo.priority !== priorityFilter) return false
      if (locationId && wo.location_id !== locationId) return false
      if (executorId) {
        const ids = [wo.assigned_to_user_id, ...(wo.assignees ?? []).map((a) => a.id)].filter(Boolean) as string[]
        if (!ids.includes(executorId)) return false
      }
      if (reviewerId && wo.reporter_id !== reviewerId) return false
      if (dateFrom && new Date(wo.created_at) < new Date(dateFrom)) return false
      if (dateTo && new Date(wo.created_at) > new Date(`${dateTo}T23:59:59`)) return false
      return true
    }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  }, [all, workType, statusFilter, priorityFilter, locationId, executorId, reviewerId, dateFrom, dateTo])

  const activeFilterCount =
    (workType !== 'all' ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0) + (priorityFilter !== 'all' ? 1 : 0) +
    (locationId ? 1 : 0) + (executorId ? 1 : 0) + (reviewerId ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0)

  function clearFilters() {
    setWorkType('all'); setStatusFilter('all'); setPriorityFilter('all')
    setLocationId(''); setExecutorId(''); setReviewerId(''); setDateFrom(''); setDateTo('')
  }

  return (
    <Box>
      {/* Filter bar */}
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap rowGap={1} sx={{ mb: 2 }}>
        <FilterChip
          icon={<BuildOutlinedIcon sx={{ fontSize: 14 }} />}
          label={workType === 'all' ? 'Type' : (TYPE_LABEL[workType] ?? 'Other')}
          active={workType !== 'all'} value={workType} onChange={setWorkType}
          options={[
            { value: 'all', label: 'All types' },
            { value: 'preventive', label: 'Preventive' },
            { value: 'reactive', label: 'Reactive' },
            { value: 'inspection', label: 'Inspection' },
            { value: 'safety', label: 'Safety' },
            { value: 'other', label: 'Other' },
          ]}
        />
        <FilterChip
          icon={<FiberManualRecordIcon sx={{ fontSize: 10 }} />}
          label={statusFilter === 'all' ? 'Status' : getStatusMeta(statusFilter).label}
          active={statusFilter !== 'all'} value={statusFilter} onChange={setStatusFilter}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'open', label: 'Open' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'closed', label: 'Closed' },
            { value: 'resolved', label: 'Resolved' },
          ]}
        />
        <FilterChip
          icon={<FlagOutlinedIcon sx={{ fontSize: 13 }} />}
          label={priorityFilter === 'all' ? 'Priority' : (PRIORITY_META[priorityFilter]?.label ?? priorityFilter)}
          active={priorityFilter !== 'all'} value={priorityFilter} onChange={setPriorityFilter}
          options={[
            { value: 'all', label: 'All priorities' },
            { value: 'critical', label: 'Critical' },
            { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' },
            { value: 'low', label: 'Low' },
          ]}
        />
        <Box sx={{ width: 170 }}>
          <SearchableSelect value={locationId} onChange={setLocationId} options={locationOptions}
            getValue={(l) => l.id} getLabel={(l) => l.name} placeholder="Location" />
        </Box>
        <Box sx={{ width: 170 }}>
          <SearchableSelect value={executorId} onChange={setExecutorId} options={executorOptions}
            getValue={(u) => u.id} getLabel={(u) => u.name} placeholder="Executed by" />
        </Box>
        <Box sx={{ width: 170 }}>
          <SearchableSelect value={reviewerId} onChange={setReviewerId} options={reviewerOptions}
            getValue={(u) => u.id} getLabel={(u) => u.name} placeholder="Reviewer" />
        </Box>
        <TextField type="date" size="small" label="From" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 152 }} />
        <TextField type="date" size="small" label="To" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ width: 152 }} />
        {activeFilterCount > 0 && (
          <Button size="small" onClick={clearFilters} startIcon={<TuneOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{ fontSize: 12, color: 'text.secondary', minWidth: 0 }}>
            Clear ({activeFilterCount})
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <ViewToggle value={view} onChange={setView} />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" rowGap={0.75} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {filtered.length} of {all.length} work order{all.length === 1 ? '' : 's'}
        </Typography>
        {/* Work-type legend — same colored dots used on the list cards
            and the chart lines below, so one legend explains both. */}
        <Stack direction="row" spacing={1.75} flexWrap="wrap" useFlexGap rowGap={0.5}>
          {WORK_TYPES.map((t) => (
            <Stack key={t} direction="row" alignItems="center" spacing={0.55}>
              <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: typeColor(t), flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{typeLabel(t)}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {woQ.isLoading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            {all.length === 0 ? 'No work orders have been executed on this asset yet.' : 'No work orders match these filters.'}
          </Typography>
        </Box>
      ) : view === 'list' ? (
        <HistoryList items={filtered} formatDate={formatDate} onNavigate={onNavigate} />
      ) : (
        <HistoryChart items={filtered} formatDate={formatDate} onNavigate={onNavigate} />
      )}
    </Box>
  )
}

// ─── View toggle (List / Scatter) ──────────────────────────────────────────

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const OPTIONS: { value: ViewMode; label: string; icon: React.ElementType }[] = [
    { value: 'list', label: 'List', icon: ViewListOutlinedIcon },
    { value: 'chart', label: 'Chart', icon: ShowChartOutlinedIcon },
  ]
  return (
    <Stack direction="row" spacing={0.25} sx={{ display: 'inline-flex', p: 0.35, borderRadius: 1.5, bgcolor: 'action.hover' }}>
      {OPTIONS.map((opt) => {
        const active = value === opt.value
        const Icon = opt.icon
        return (
          <Box key={opt.value} component="button" onClick={() => onChange(opt.value)}
            sx={{
              border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 1.25, py: 0.5, borderRadius: 1,
              fontSize: 12, fontWeight: 600, letterSpacing: '-0.005em',
              transition: 'background-color 120ms ease, color 120ms ease',
              bgcolor: active ? 'background.paper' : 'transparent',
              color: active ? 'primary.main' : 'text.secondary',
              boxShadow: active ? '0 1px 2px rgba(15,23,42,0.08)' : 'none',
              '&:hover': !active ? { color: 'text.primary' } : undefined,
            }}>
            <Icon sx={{ fontSize: 15 }} />
            {opt.label}
          </Box>
        )
      })}
    </Stack>
  )
}

// ─── List view ──────────────────────────────────────────────────────────────

function HistoryList({ items, formatDate, onNavigate }: {
  items: AssetWorkOrderHistoryEntry[]
  formatDate: (d: Date | string, includeTime?: boolean) => string
  onNavigate: (path: string) => void
}) {
  return (
    <Stack spacing={1.75}>
      {items.map((wo) => <HistoryCard key={wo.id} wo={wo} formatDate={formatDate} onClick={() => onNavigate(`/work-orders/${wo.id}`)} />)}
    </Stack>
  )
}

function HistoryCard({ wo, formatDate, onClick, dense = false }: {
  wo: AssetWorkOrderHistoryEntry
  formatDate: (d: Date | string, includeTime?: boolean) => string
  onClick: () => void
  /** Stacks the identity rows into a single column instead of wrapping
   *  them horizontally — used in the chart drill-down panel, which is
   *  too narrow for the wide layout to wrap cleanly. */
  dense?: boolean
}) {
  const st = getStatusMeta(wo.status)
  const pr = PRIORITY_META[wo.priority] ?? { label: wo.priority, color: '#64748B', bg: '#F8FAFC' }
  const finishedAt = wo.closed_at ?? wo.resolved_at ?? null
  const executors = (wo.assignees ?? []).length > 0
    ? wo.assignees!
    : (wo.assigned_to_user_id ? [{ id: wo.assigned_to_user_id, name: wo.assigned_to_user_name ?? 'Unknown', avatar_url: wo.assigned_to_user_avatar ?? null }] : [])
  // Who actually acknowledged: the captured signature when one was
  // required, otherwise the reporter/reviewer — they're the only one
  // (besides an admin override) allowed to acknowledge in the first
  // place, so it's an honest attribution even without a signature.
  const acknowledgedBy = wo.acknowledge_signature?.name ?? (wo.status === 'resolved' ? wo.reporter_name : null)
  const acknowledgedAt = wo.acknowledge_signature?.signed_at ?? (wo.status === 'resolved' ? wo.resolved_at : null)
  const approvals = wo.approval_steps ?? []

  return (
    <Box onClick={onClick} sx={{
      position: 'relative',
      cursor: 'pointer', borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper', pl: 3, pr: dense ? 2 : 2.5, py: dense ? 1.75 : 2,
      boxShadow: (t) => t.palette.mode === 'light' ? '0 1px 2px rgba(15,23,42,0.05)' : 'none',
      transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
      '&:hover': { borderColor: 'rgba(0,0,0,0.18)', boxShadow: '0 8px 20px -12px rgba(15,23,42,0.28)', transform: 'translateY(-1px)' },
      // Priority accent — same inset-bar treatment as the calendar
      // agenda cards, so a card reads as its own surface at a glance
      // instead of blending into its neighbors.
      '&::before': {
        content: '""', position: 'absolute', left: 0, top: 10, bottom: 10,
        width: 3, borderRadius: 99, bgcolor: pr.color,
      },
    }}>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5} sx={{ mb: 1.25 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', mb: 0.25 }} noWrap>{wo.title}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>
            {wo.wo_code ?? wo.id.slice(0, 8)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75} flexShrink={0}>
          <Box component="span" sx={{ fontSize: 10.5, fontWeight: 700, px: 0.85, py: 0.35, borderRadius: 99, bgcolor: st.bg, color: st.color }}>{st.label}</Box>
          <Box component="span" sx={{ fontSize: 10.5, fontWeight: 700, px: 0.85, py: 0.35, borderRadius: 99, bgcolor: pr.bg, color: pr.color }}>{pr.label}</Box>
          <Stack direction="row" alignItems="center" spacing={0.5}
            sx={{ fontSize: 10.5, fontWeight: 700, px: 0.85, py: 0.35, borderRadius: 99, bgcolor: 'action.hover', color: 'text.secondary' }}>
            <Box sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: typeColor(wo.work_type), flexShrink: 0 }} />
            <Box component="span">{typeLabel(wo.work_type)}</Box>
          </Stack>
        </Stack>
      </Stack>

      {/* Identity rows */}
      <Stack direction={dense ? 'column' : 'row'} flexWrap={dense ? undefined : 'wrap'} useFlexGap={!dense}
        columnGap={dense ? undefined : 3} rowGap={dense ? 0.85 : 1}>
        {wo.location_name && (
          <IdentityRow icon={<LocationOnOutlinedIcon sx={{ fontSize: 14 }} />} label="Location"
            value={[wo.site_name, wo.location_name].filter(Boolean).join(' · ')} />
        )}
        <IdentityRow icon={<AssignmentIndOutlinedIcon sx={{ fontSize: 14 }} />} label="Executed by"
          value={executors.length > 0
            ? <Stack direction="row" alignItems="center" spacing={0.5}>
                {executors.slice(0, 3).map((a) => (
                  <Tooltip key={a.id} title={a.name} arrow>
                    <span><UserAvatar name={a.name} avatarUrl={a.avatar_url} size={20} /></span>
                  </Tooltip>
                ))}
                <Typography sx={{ fontSize: 12.5 }}>
                  {executors.length === 1 ? executors[0]!.name : `${executors.length} people`}
                </Typography>
              </Stack>
            : (wo.assigned_to_team_name ?? 'Unassigned')}
        />
        <IdentityRow icon={<RateReviewOutlinedIcon sx={{ fontSize: 14 }} />} label="Reviewer" value={wo.reporter_name ?? '—'} />
        {acknowledgedBy && (
          <IdentityRow icon={<VerifiedOutlinedIcon sx={{ fontSize: 14 }} />} label="Acknowledged by"
            value={`${acknowledgedBy}${acknowledgedAt ? ` · ${formatDate(acknowledgedAt)}` : ''}`} />
        )}
      </Stack>

      {/* Approval chain */}
      {approvals.length > 0 && (
        <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap columnGap={1} rowGap={0.5} sx={{ mt: 1.25 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
            Approvals
          </Typography>
          {approvals.map((step) => {
            const color = APPROVAL_STATUS_COLOR[step.status] ?? '#64748B'
            const Icon = step.status === 'approved' ? CheckCircleOutlinedIcon : step.status === 'rejected' ? CancelOutlinedIcon : ScheduleOutlinedIcon
            return (
              <Stack key={step.id} direction="row" alignItems="center" spacing={0.4}
                sx={{ px: 0.85, py: 0.25, borderRadius: 99, bgcolor: `${color}14` }}>
                <Icon sx={{ fontSize: 12, color }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color }}>{step.approver_name}</Typography>
              </Stack>
            )
          })}
        </Stack>
      )}

      {/* Footer dates */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1.25, pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Created {formatDate(wo.created_at)}</Typography>
        {finishedAt && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>· Finished {formatDate(finishedAt)}</Typography>}
      </Stack>
    </Box>
  )
}

function IdentityRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{ color: 'text.disabled', display: 'inline-flex' }}>{icon}</Box>
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{label}:</Typography>
      {typeof value === 'string' ? <Typography sx={{ fontSize: 12.5, fontWeight: 500 }}>{value}</Typography> : value}
    </Stack>
  )
}

// ─── Chart view ─────────────────────────────────────────────────────────────
//
// One line per work type, X = time bucket, Y = count of work orders
// created in that bucket. The bucket size (day/week/month/quarter/
// year) is picked automatically from how wide the filtered date range
// actually is, so a single busy month reads as daily bars while a
// multi-year history reads as yearly ones. Clicking a point (or
// anywhere along its axis column) selects that bucket and lists its
// work orders in the panel on the right; hovering shows the built-in
// per-series tooltip for that bucket.
type Granularity = 'day' | 'week' | 'month' | 'quarter' | 'year'

function pickGranularity(spanDays: number): Granularity {
  if (spanDays <= 31) return 'day'
  if (spanDays <= 120) return 'week'
  if (spanDays <= 730) return 'month'
  if (spanDays <= 365 * 6) return 'quarter'
  return 'year'
}

function startOfBucket(d: Date, gran: Granularity): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  if (gran === 'day') return x
  if (gran === 'week') { x.setDate(x.getDate() - x.getDay()); return x }
  if (gran === 'month') return new Date(x.getFullYear(), x.getMonth(), 1)
  if (gran === 'quarter') return new Date(x.getFullYear(), Math.floor(x.getMonth() / 3) * 3, 1)
  return new Date(x.getFullYear(), 0, 1)
}

function nextBucket(d: Date, gran: Granularity): Date {
  const x = new Date(d)
  if (gran === 'day') x.setDate(x.getDate() + 1)
  else if (gran === 'week') x.setDate(x.getDate() + 7)
  else if (gran === 'month') x.setMonth(x.getMonth() + 1)
  else if (gran === 'quarter') x.setMonth(x.getMonth() + 3)
  else x.setFullYear(x.getFullYear() + 1)
  return x
}

function bucketLabel(d: Date, gran: Granularity): string {
  if (gran === 'day' || gran === 'week') return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  if (gran === 'month') return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
  if (gran === 'quarter') return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`
  return String(d.getFullYear())
}

const GRANULARITY_NOUN: Record<Granularity, string> = {
  day: 'day', week: 'week', month: 'month', quarter: 'quarter', year: 'year',
}

function HistoryChart({ items, formatDate, onNavigate }: {
  items: AssetWorkOrderHistoryEntry[]
  formatDate: (d: Date | string, includeTime?: boolean) => string
  onNavigate: (path: string) => void
}) {
  const [selectedBucket, setSelectedBucket] = useState<number | null>(null)

  const { buckets, granularity } = useMemo(() => {
    if (items.length === 0) return { buckets: [] as Date[], granularity: 'month' as Granularity }
    const times = items.map((wo) => new Date(wo.created_at).getTime())
    const minD = new Date(Math.min(...times))
    const maxD = new Date(Math.max(...times))
    const spanDays = Math.max(1, (maxD.getTime() - minD.getTime()) / 86_400_000)
    const gran = pickGranularity(spanDays)

    const list: Date[] = []
    let cursor = startOfBucket(minD, gran)
    const end = startOfBucket(maxD, gran)
    // Hard cap so a mis-scoped range can't spin this into an
    // effectively-infinite loop — 400 buckets already dwarfs any
    // sane granularity/range pairing (400 years, 400 days, etc.).
    let guard = 0
    while (cursor.getTime() <= end.getTime() && guard < 400) {
      list.push(new Date(cursor))
      cursor = nextBucket(cursor, gran)
      guard++
    }
    return { buckets: list, granularity: gran }
  }, [items])

  const xLabels = useMemo(() => buckets.map((d) => bucketLabel(d, granularity)), [buckets, granularity])

  const series = useMemo(() => {
    const counts: Record<string, number[]> = {}
    WORK_TYPES.forEach((t) => { counts[t] = buckets.map(() => 0) })
    items.forEach((wo) => {
      const b = startOfBucket(new Date(wo.created_at), granularity).getTime()
      const idx = buckets.findIndex((d) => d.getTime() === b)
      if (idx === -1) return
      const t = WORK_TYPES.includes(wo.work_type as any) ? wo.work_type : 'other'
      const arr = counts[t]!
      arr[idx] = (arr[idx] ?? 0) + 1
    })
    return WORK_TYPES
      .map((t) => ({
        id: t,
        label: typeLabel(t),
        color: typeColor(t),
        data: counts[t]!,
        area: true,
        showMark: true,
        // Straight segments read cleaner than a smoothed curve once
        // counts swing between 0 and a handful per bucket — a smoothed
        // curve balloons those jumps into exaggerated arcs.
        curve: 'linear' as const,
        // Force every series onto the same circular marker — by
        // default each series cycles through a different shape
        // (square, diamond, …), which fights the color legend instead
        // of reinforcing it.
        shape: 'circle' as const,
        valueFormatter: (v: number | null) => v == null ? '' : `${v} ${typeLabel(t)}`,
      }))
      .filter((s) => s.data.some((v) => v > 0))
  }, [items, buckets, granularity])

  // Everything created within the selected bucket's window, across all
  // types — the right-panel drill-down list.
  const bucketItems = useMemo(() => {
    if (selectedBucket == null) return []
    const start = buckets[selectedBucket]
    if (!start) return []
    const end = nextBucket(start, granularity)
    return items
      .filter((wo) => {
        const t = new Date(wo.created_at).getTime()
        return t >= start.getTime() && t < end.getTime()
      })
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  }, [selectedBucket, buckets, granularity, items])

  function selectFromAxisData(data: ChartsAxisData | null) {
    if (data) setSelectedBucket(data.dataIndex)
  }

  if (buckets.length === 0 || series.length === 0) {
    return (
      <Box sx={{ py: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Nothing to chart for these filters yet.</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{
      display: 'flex', flexDirection: { xs: 'column', md: 'row' },
      border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden',
    }}>
      {/* Chart */}
      <Box sx={{ flex: 1, minWidth: 0, p: 3 }}>
        <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 2 }}>
          Grouped by {GRANULARITY_NOUN[granularity]} — click a point to see that {GRANULARITY_NOUN[granularity]}'s work orders
        </Typography>
        <Box sx={{ height: 400 }}>
          <LineChart
            series={series}
            xAxis={[{
              scaleType: 'point', data: xLabels, id: 'bucket-axis',
              disableLine: true, tickLabelStyle: { fontSize: 10.5 },
            }]}
            yAxis={[{
              label: 'Work orders', tickMinStep: 1, disableLine: true, disableTicks: true,
              tickLabelStyle: { fontSize: 10.5 },
            }]}
            margin={{ top: 20, right: 24, bottom: 36, left: 52 }}
            grid={{ horizontal: true }}
            axisHighlight={{ x: 'line' }}
            hideLegend
            onAxisClick={(_, data) => selectFromAxisData(data)}
            sx={{
              '& .MuiChartsAxis-tickLabel': { fill: (t) => t.palette.text.secondary },
              '& .MuiChartsAxis-line': { stroke: (t) => t.palette.divider },
              '& .MuiChartsGrid-line': { stroke: (t) => t.palette.divider, strokeDasharray: '3 5' },
              '& .MuiChartsAxisHighlight-root': { stroke: (t) => t.palette.text.disabled, strokeDasharray: '2 3' },
              '& .MuiAreaElement-root': { fillOpacity: 0.14 },
              '& .MuiLineElement-root': { strokeWidth: 2.5 },
              '& .MuiMarkElement-root': { strokeWidth: 2, r: 4.5, fill: (t) => t.palette.background.paper },
              '& .MuiMarkElement-root:hover': { r: 6 },
              '& .MuiLineElement-root, & .MuiMarkElement-root': { cursor: 'pointer' },
              ...chartTooltipSx,
            }}
          />
        </Box>
      </Box>

      {/* Drill-down panel */}
      <Box sx={{
        width: { xs: '100%', md: 340 }, flexShrink: 0,
        borderLeft: { xs: 'none', md: '1px solid' }, borderTop: { xs: '1px solid', md: 'none' }, borderColor: 'divider',
        display: 'flex', flexDirection: 'column', minHeight: 0,
        bgcolor: (t) => (t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.015)'),
      }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>
            {selectedBucket != null && buckets[selectedBucket] ? bucketLabel(buckets[selectedBucket]!, granularity) : 'No point selected'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
            {selectedBucket == null
              ? 'Click a point on the chart to see its work orders.'
              : `${bucketItems.length} work order${bucketItems.length === 1 ? '' : 's'}`}
          </Typography>
        </Box>
        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2 }}>
          {selectedBucket != null && bucketItems.length === 0 && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', textAlign: 'center', py: 3 }}>Nothing in this {GRANULARITY_NOUN[granularity]}.</Typography>
          )}
          {bucketItems.map((wo) => (
            <HistoryCard key={wo.id} wo={wo} formatDate={formatDate} onClick={() => onNavigate(`/work-orders/${wo.id}`)} dense />
          ))}
        </Stack>
      </Box>
    </Box>
  )
}
